// App state, the web counterpart of the iOS AppModel (no Health, Calendar or offline queue).
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Backend, Checkin, ConsentKind, Profile, Session, Sharing, TrainerLink, TrainerNote, Upcoming } from './backend/types.ts';
import { suggestedMuscles } from './lib/activities.ts';
import { groupLoads, muscleLoads } from './lib/load.ts';

export type Phase = 'launching' | 'onboarding' | 'main';

/** YYYY-MM-DD in the given time zone. */
export function dayKey(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
}

export function newSession(kind: Session['kind']): Session {
  // Default: an hour-long session that started an hour ago (on a 5-minute mark).
  const now = Math.floor(Date.now() / 300_000) * 300_000;
  return {
    id: crypto.randomUUID(), kind, activity_type: kind === 'trainer' ? 'strength' : 'running', source: 'manual', status: 'pending',
    started_at: new Date(now - 3_600_000).toISOString(), ended_at: new Date(now).toISOString(),
    intensity: null, recovery: null, note: null, muscles: kind === 'solo' ? suggestedMuscles('running') : [],
  };
}

function useModelState(backend: Backend) {
  const [phase, setPhase] = useState<Phase>('launching');
  const [profile, setProfile] = useState<Profile | null>(null);
  const [consents, setConsents] = useState<Set<ConsentKind>>(new Set());
  const [sessions, setSessions] = useState<Session[]>([]);
  const [checkins, setCheckins] = useState<Checkin[]>([]);
  const [upcoming, setUpcoming] = useState<Upcoming[]>([]);
  const [notes, setNotes] = useState<TrainerNote[]>([]);
  const [links, setLinks] = useState<TrainerLink[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 2500);
    return () => clearTimeout(t);
  }, [notice]);

  const timeZone = profile?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const attempt = useCallback(async <T,>(fn: () => Promise<T>, message?: string): Promise<T | undefined> => {
    try {
      const out = await fn();
      setError(null);
      return out;
    } catch (e) {
      setError(message ?? (e instanceof Error ? e.message : String(e)));
      return undefined;
    }
  }, []);

  const refresh = useCallback(async () => {
    setBusy(true);
    await attempt(async () => {
      const since = new Date(Date.now() - 60 * 86_400_000);
      const [p, c, s, ch, u, n, l] = await Promise.all([
        backend.profile(), backend.consents(), backend.sessions(since),
        backend.checkins(dayKey(since, timeZone)), backend.upcoming(), backend.notes(), backend.links(),
      ]);
      if (p) setProfile(p);
      setEmail(await backend.email().catch(() => null));
      setConsents(c); setSessions(s); setCheckins(ch); setUpcoming(u); setNotes(n); setLinks(l);
    }, "Couldn't reach Recover. Check your connection and pull to refresh.");
    setBusy(false);
  }, [attempt, backend, timeZone]);

  const start = useCallback(async () => {
    const uid = await backend.userId().catch(() => null);
    const p = uid ? await backend.profile().catch(() => null) : null;
    if (!p) { setPhase('onboarding'); return; }
    const c = await backend.consents().catch(() => new Set<ConsentKind>());
    setProfile(p); setConsents(c);
    const ready = p.onboarded_at && c.has('terms') && c.has('health_data');
    setPhase(ready ? 'main' : 'onboarding');
    if (ready) await refresh();
  }, [backend, refresh]);

  useEffect(() => { void start(); }, [start]);

  const reset = useCallback(() => {
    setProfile(null); setConsents(new Set()); setSessions([]); setCheckins([]); setUpcoming([]); setNotes([]); setLinks([]);
    setPhase('onboarding');
  }, []);

  const loads = useMemo(() => muscleLoads(sessions), [sessions]);
  const today = dayKey(new Date(), timeZone);

  return {
    backend, phase, profile, email, notice, setNotice, consents, sessions, checkins, upcoming, notes, links, error, busy, timeZone, loads,
    groupLoads: useMemo(() => groupLoads(loads), [loads]),
    logged: useMemo(() => sessions.filter((s) => s.status === 'logged'), [sessions]),
    todaysCheckin: checkins.find((c) => c.day === today) ?? null,
    pinnedNotes: notes.filter((n) => n.raise_next_session && !n.raised_at),
    setError,
    refresh,

    async signedIn() {
      const [p, c] = await Promise.all([backend.profile(), backend.consents()]);
      setProfile(p); setConsents(c);
    },
    async updateProfile(change: Partial<Omit<Profile, 'id'>>) {
      setProfile((p) => (p ? { ...p, ...change } : p));
      await attempt(() => backend.updateProfile(change));
    },
    async setConsent(kind: ConsentKind, granted: boolean) {
      await attempt(async () => {
        if (granted) await backend.grantConsent(kind);
        else await backend.revokeConsent(kind);
        // Turning sharing off stops digests: clear the sessions they are timed from.
        if (kind === 'trainer_sharing' && !granted) { await backend.syncUpcoming([]); setUpcoming([]); }
        setConsents(await backend.consents());
      });
    },
    async finishOnboarding() {
      await attempt(() => backend.updateProfile({ onboarded_at: new Date().toISOString() }));
      setPhase('main');
      await refresh();
    },
    async log(session: Session) {
      const s = { ...session, status: 'logged' as const };
      const ok = await attempt(async () => { await backend.logSession(s); return true; }, "Couldn't save the session. Try again.");
      if (ok) {
        setSessions((all) => [s, ...all.filter((x) => x.id !== s.id)].sort((a, b) => b.started_at.localeCompare(a.started_at)));
        setNotice(session.status === 'logged' ? 'Session updated' : 'Session saved');
      }
      return !!ok;
    },
    async remove(session: Session) {
      const s = { ...session, status: 'dismissed' as const };
      if (await attempt(async () => { await backend.logSession(s); return true; })) {
        setSessions((all) => all.map((x) => (x.id === s.id ? s : x)));
        setNotice('Session deleted');
      }
    },
    async checkIn(rating: number) {
      const c = { day: today, rating };
      setCheckins((all) => [c, ...all.filter((x) => x.day !== today)]);
      await attempt(() => backend.saveCheckin(c));
    },
    async setUpcoming(list: { calendar_event_id: string; title: string; starts_at: string }[]) {
      if (await attempt(async () => { await backend.syncUpcoming(list); return true; })) {
        setUpcoming(await backend.upcoming());
        setNotice('Next session updated');
      }
    },
    async updateSharing(link: TrainerLink, sharing: Sharing) {
      setLinks((all) => all.map((l) => (l.id === link.id ? { ...l, ...sharing } : l)));
      await attempt(() => backend.updateSharing(link.id, sharing));
    },
    async revoke(link: TrainerLink) {
      setLinks((all) => all.filter((l) => l.id !== link.id));
      await attempt(() => backend.revokeLink(link.id));
    },
    createInvite: () => attempt(() => backend.createInvite()),
    async signOut() { await backend.signOut().catch(() => {}); reset(); },
    async deleteAccount() {
      const ok = await attempt(async () => { await backend.deleteAccount(); return true; }, "Couldn't delete your account. Try again or email us.");
      if (ok) reset();
      return !!ok;
    },
  };
}

export type Model = ReturnType<typeof useModelState>;
const Ctx = createContext<Model | null>(null);

export function ModelProvider({ backend, children }: { backend: Backend; children: ReactNode }) {
  const model = useModelState(backend);
  return <Ctx.Provider value={model}>{children}</Ctx.Provider>;
}

export function useModel(): Model {
  const m = useContext(Ctx);
  if (!m) throw new Error('useModel outside ModelProvider');
  return m;
}
